/*
 * Meter: AI credits this period. Shows what is left of the monthly allowance plus bought packs, as one bar
 * with two segments (included in teal, extra in teal-400), and the figure left.
 *   <Meter used={742} included={1200} extra={100} label={t('ai-credits:left')} />
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { useFormat } from '@/i18n/useFormat'

export function Meter({
  used,
  included,
  extra = 0,
  label,
  footer,
  className,
}: {
  used: number
  included: number
  extra?: number
  label?: ReactNode
  footer?: ReactNode
  className?: string
}) {
  const { t } = useTranslation('common')
  const f = useFormat()
  const total = included + extra
  const left = Math.max(0, total - used)
  const pct = (n: number) => `${total > 0 ? Math.min(100, (n / total) * 100) : 0}%`
  const usedIncluded = Math.min(used, included)
  const usedExtra = Math.max(0, used - included)
  const low = total > 0 && left / total < 0.1
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-baseline justify-between gap-3">
        {label ? <span className="text-13 font-medium text-muted">{label}</span> : <span />}
        <span className="flex items-baseline gap-1.5">
          <span
            className={cn(
              'font-display figures text-24 font-semibold',
              low ? 'text-warm-700' : 'text-ink-deep',
            )}
          >
            {f.number(left)}
          </span>
          <span className="text-13 text-subtle">{t('ui.meter.ofTotal', { total: f.number(total) })}</span>
        </span>
      </div>
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={left}
        aria-label={typeof label === 'string' ? label : undefined}
        className="relative flex h-2.5 overflow-hidden rounded-full bg-control-strong"
      >
        {/* remaining included, then remaining extra; used is the empty track */}
        <span
          className="h-full bg-teal-600 transition-[width] duration-500"
          style={{ width: pct(included - usedIncluded) }}
        />
        <span
          className="h-full bg-teal-400 transition-[width] duration-500"
          style={{ width: pct(extra - usedExtra) }}
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-12 text-subtle">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-full bg-teal-600" aria-hidden="true" />
          {t('ui.meter.included', { n: f.number(included) })}
        </span>
        {extra > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-teal-400" aria-hidden="true" />
            {t('ui.meter.extra', { n: f.number(extra) })}
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-full bg-control-strong ring-1 ring-line" aria-hidden="true" />
          {t('ui.meter.used', { n: f.number(used) })}
        </span>
        {footer}
      </div>
    </div>
  )
}
